// 游戏核心逻辑

import type { Cell, LevelConfig, PieceData, GamePhase, CellState } from '../types.js';
import { cellEq, cellInList, computeBoundingBox, getPieceNeighbors, isEdgeCell, shuffle, normalizeCells, cellToPixel, drawHexPath } from '../utils/hexUtils.js';

export interface PlacedPiece {
  pieceId: string;
  cells: Cell[];   // 绝对坐标（已放置位置）
  localCells: Cell[]; // 归一化的相对坐标（用于匹配）
}

export interface CellInfo {
  state: CellState;
  pieceId: string | null;  // 哪个碎片放在这里
}

export class PuzzleGame {
  config: LevelConfig;
  phase: GamePhase = 'initial';
  cells: Map<string, CellInfo> = new Map();
  placedPieces: PlacedPiece[] = [];
  piecePool: PieceData[] = [];           // 所有未放置的碎片（除了displayPieces）
  displayPieces: PieceData[] = [];        // 碎片区展示的3个碎片
  initialPieces: PieceData[] = [];        // 初始3个碎片（用于第一阶段）
  unlockedCells: Cell[] = [];             // 所有已开启的cells（包括已放置和未放置）
  totalCells: number = 0;
  startTime: number | null = null;
  elapsedTime: number = 0;
  remainingTime: number = 0;
  items: { magnet: number; hint: number } = { magnet: 1, hint: 1 };

  constructor(config: LevelConfig) {
    this.config = config;
    this.initializeGrid();
    this.remainingTime = config.timeLimit;
  }

  private key(col: number, row: number): string {
    return `${col},${row}`;
  }

  private initializeGrid(): void {
    const { cols, rows } = this.config.grid;
    // 初始化所有cells为hidden
    for (let c = 0; c < cols; c++) {
      const rowCount = (c % 2 === 0) ? rows : rows - 1;
      for (let r = 0; r < rowCount; r++) {
        this.cells.set(this.key(c, r), { state: 'hidden', pieceId: null });
        this.totalCells++;
      }
    }

    // 初始选3个边缘cell，找到对应的碎片
    this.piecePool = [...this.config.pieces];

    // 找边缘cells
    const edgeCells: Cell[] = [];
    for (const piece of this.config.pieces) {
      for (const cell of piece.cells) {
        if (isEdgeCell(cell.col, cell.row, cols, rows) && !cellInList(cell, edgeCells)) {
          edgeCells.push(cell);
        }
      }
    }

    // 从边缘cells中选3个不同的cell，然后找到包含它们的碎片
    const shuffledEdges = shuffle(edgeCells);
    const selectedEdgeCells: Cell[] = [];

    for (const edgeCell of shuffledEdges) {
      if (selectedEdgeCells.length >= 3) break;
      // 检查这个边缘cell是否已经被选中
      if (!cellInList(edgeCell, selectedEdgeCells)) {
        // 检查是否和已选cell来自同一个碎片
        const piece = this.findPieceContainingCell(edgeCell);
        if (piece && !this.initialPieces.includes(piece)) {
          this.initialPieces.push(piece);
          selectedEdgeCells.push(edgeCell);
        }
      }
    }

    // 如果边缘cells不够3个，从可用碎片中补齐
    if (this.initialPieces.length < 3) {
      for (const piece of this.piecePool) {
        if (!this.initialPieces.includes(piece)) {
          this.initialPieces.push(piece);
          if (this.initialPieces.length >= 3) break;
        }
      }
    }

    // 从piecePool中移除initialPieces
    for (const piece of this.initialPieces) {
      this.piecePool = this.piecePool.filter(p => p.id !== piece.id);
    }

    // 设置初始pieces的cells为opened
    for (const piece of this.initialPieces) {
      for (const cell of piece.cells) {
        const info = this.cells.get(this.key(cell.col, cell.row));
        if (info && info.state === 'hidden') {
          info.state = 'opened';
          if (!cellInList(cell, this.unlockedCells)) {
            this.unlockedCells.push(cell);
          }
        }
      }
    }

    // 补货：piecePool 里"所有 cells 都是 opened"的碎片入碎片区
    this.refillDisplay();

    // 初始碎片区显示这3个
    this.displayPieces = [...this.initialPieces];
  }

  private findPieceContainingCell(cell: Cell): PieceData | null {
    for (const piece of this.config.pieces) {
      if (cellInList(cell, piece.cells)) return piece;
    }
    return null;
  }

  // 尝试放置碎片到地图
  // piece: 要放置的碎片
  // anchorCell: 用户松手位置对应的地图cell
  // 判定：anchorCell 是否在 piece.cells 中（即碎片被拖到了正确位置附近）
  tryPlacePiece(piece: PieceData, anchorCell: Cell): { success: boolean } {
    const normalized = normalizeCells(piece.cells);

    // 检查 anchorCell 是否在 piece.cells 中
    // 这意味着碎片被拖到了正确的位置区域
    if (!cellInList(anchorCell, piece.cells)) {
      return { success: false };
    }

    // 检查 piece.cells 中所有cells是否都已开启
    for (const tc of piece.cells) {
      const info = this.cells.get(this.key(tc.col, tc.row));
      if (!info || info.state !== 'opened') {
        return { success: false };
      }
    }

    // 放置成功！锁定这些cells
    for (const tc of piece.cells) {
      const info = this.cells.get(this.key(tc.col, tc.row));
      if (info) {
        info.state = 'locked';
        info.pieceId = piece.id;
      }
    }
    this.placedPieces.push({
      pieceId: piece.id,
      cells: [...piece.cells],
      localCells: normalized,
    });

    // 从displayPieces中移除
    this.displayPieces = this.displayPieces.filter(p => p.id !== piece.id);

    // 解锁：邻居格子属于哪个未放置的碎片，就把那个碎片的所有 hidden cells 都设为 opened
    const neighbors = getPieceNeighbors(piece.cells);
    const neighborPieceIds = new Set<string>();
    for (const n of neighbors) {
      const neighborPiece = this.findPieceContainingCell(n);
      if (neighborPiece && !this.placedPieces.some(pp => pp.pieceId === neighborPiece.id)) {
        neighborPieceIds.add(neighborPiece.id);
      }
    }
    for (const pid of neighborPieceIds) {
      const p = this.config.pieces.find(cp => cp.id === pid);
      if (p) this.unlockPieceAllCells(p);
    }

    // 补货
    this.refillDisplay();

    // 检查阶段转换
    this.checkPhaseTransition();

    // 开始计时（第一次放置）
    if (this.startTime === null) {
      this.startTime = Date.now();
    }

    return { success: true };
  }

  // 解锁整个碎片的所有 hidden cells
  private unlockPieceAllCells(piece: PieceData): void {
    for (const cell of piece.cells) {
      const info = this.cells.get(this.key(cell.col, cell.row));
      if (info && info.state === 'hidden') {
        info.state = 'opened';
        if (!cellInList(cell, this.unlockedCells)) {
          this.unlockedCells.push(cell);
        }
      }
    }
  }

  private refillDisplay(): void {
    // 只要碎片的 cells 中至少一个是 opened（说明这块区域开始露出了），就可以进碎片区
    while (this.displayPieces.length < 3 && this.piecePool.length > 0) {
      const available = this.piecePool.filter(p => this.isPieceAnyCellUnlocked(p));
      if (available.length === 0) break;
      const idx = Math.floor(Math.random() * available.length);
      const piece = available[idx];
      this.piecePool = this.piecePool.filter(p => p.id !== piece.id);
      this.displayPieces.push(piece);
    }
  }

  private isPieceAnyCellUnlocked(piece: PieceData): boolean {
    for (const cell of piece.cells) {
      const info = this.cells.get(this.key(cell.col, cell.row));
      if (info && info.state === 'opened') return true;
    }
    return false;
  }

  private checkPhaseTransition(): void {
    // 检查胜利
    if (this.placedPieces.length === this.config.pieces.length) {
      this.phase = 'won';
      return;
    }

    // 如果还是initial阶段，检查initialPieces是否都放置了
    if (this.phase === 'initial') {
      const placedInitial = this.initialPieces.filter(ip =>
        this.placedPieces.some(pp => pp.pieceId === ip.id)
      );
      if (placedInitial.length === this.initialPieces.length) {
        this.phase = 'playing';
        // 把所有未放置的可用碎片都放回piecePool，让补货逻辑可以选
        // 不做特殊处理，补货逻辑会自动选
      }
    }
  }

  // 磁力道具：自动吸附displayPieces中的第一个碎片到正确位置
  useMagnet(): PieceData | null {
    if (this.items.magnet <= 0 || this.displayPieces.length === 0) return null;
    const piece = this.displayPieces[0];
    // 直接放置到piece.cells的位置
    const anchor = piece.cells[0];
    const result = this.tryPlacePiece(piece, anchor);
    if (result.success) {
      this.items.magnet--;
      return piece;
    }
    return null;
  }

  // 获取查看道具显示的数据：displayPieces的正确位置
  useHint(): { piece: PieceData; correctCells: Cell[] }[] | null {
    if (this.items.hint <= 0) return null;
    this.items.hint--;
    // 显示5秒，在UI层处理
    return this.displayPieces.map(p => ({ piece: p, correctCells: [...p.cells] }));
  }

  // 更新倒计时
  updateTime(deltaMs: number): boolean {
    if (this.startTime === null) return true; // 还没开始计时
    if (this.phase === 'won' || this.phase === 'lost') return false; // 游戏结束，暂停计时
    this.elapsedTime += deltaMs;
    this.remainingTime = Math.max(0, this.config.timeLimit - this.elapsedTime / 1000);
    if (this.remainingTime <= 0) {
      this.phase = 'lost';
      return false;
    }
    return true;
  }

  // 格式化时间
  static formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  // 重开游戏
  static fromConfig(config: LevelConfig): PuzzleGame {
    return new PuzzleGame(config);
  }
}
